import { useNavigate, Link } from "react-router-dom";
import { Shuffle, MessageCircle, MessageSquare, BarChart2, Radio, Gamepad2 } from "lucide-react";
import SearchAutocomplete from "./SearchAutocomplete";

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/a8872220-d49e-4b2e-8c15-4ea20ad32e2b/images/a4bf464029d773b75b0becd99b54cc1769838b9a12bb58542445be8159d4d610.png";

const CHIPS = [
  {
    label: "What to Watch Tonight",
    action: { type: "link", to: "/list/trending-india" },
  },
  {
    label: "New OTT Releases",
    action: { type: "link", to: "/list/recently-released-india" },
  },
  {
    label: "Hidden Gems",
    action: { type: "link", to: "/list/hidden-gems" },
  },
  {
    label: "Family-Safe",
    action: { type: "link", to: "/discover?family_safe=1" },
  },
  {
    label: "Edge of Seat",
    action: { type: "search", q: "edge of seat gripping thriller suspense" },
  },
  {
    label: "Feel-Good",
    action: { type: "link", to: "/collection/feel-good" },
  },
  {
    label: "Hindi Movies",
    action: { type: "link", to: "/discover?language=hi" },
  },
  {
    label: "K-Drama",
    action: { type: "link", to: "/collection/k-drama-obsession" },
  },
];

const SOCIAL_CARDS = [
  {
    label: "Discussions",
    to: "/social?tab=discussions",
    icon: MessageSquare,
    color: "#E50914",
    glow: "rgba(229,9,20,0.25)",
    border: "rgba(229,9,20,0.35)",
    testid: "hero-social-discussions",
  },
  {
    label: "Poll",
    to: "/social?tab=polls",
    icon: BarChart2,
    color: "#007AFF",
    glow: "rgba(0,122,255,0.25)",
    border: "rgba(0,122,255,0.35)",
    testid: "hero-social-poll",
  },
  {
    label: "Live Chat",
    to: "/social?tab=chat",
    icon: Radio,
    color: "#A855F7",
    glow: "rgba(168,85,247,0.25)",
    border: "rgba(168,85,247,0.35)",
    testid: "hero-social-livechat",
  },
  {
    label: "Games",
    to: "/social?tab=games",
    icon: Gamepad2,
    color: "#22C55E",
    glow: "rgba(34,197,94,0.25)",
    border: "rgba(34,197,94,0.35)",
    testid: "hero-social-games",
  },
];

export default function HeroSection() {
  const navigate = useNavigate();

  const onChip = (chip) => {
    if (chip.action.type === "search") navigate(`/search?q=${encodeURIComponent(chip.action.q)}`);
    else navigate(chip.action.to);
  };

  return (
    <section
      className="relative w-full min-h-[88vh] flex items-center overflow-hidden fm-grain"
      data-testid="hero-section"
    >
      {/* Backdrop */}
      <img src={HERO_BG} alt="" className="absolute inset-0 w-full h-full object-cover scale-105" />
      <div className="absolute inset-0 fm-hero-overlay-radial" />
      <div className="absolute inset-0 fm-hero-overlay" />

      <div className="relative z-10 w-full mx-auto px-6 lg:px-10 py-20">
        <div className="max-w-3xl mx-auto text-center flex flex-col items-center">

          {/* Badge pill */}
          <div className="inline-flex items-center px-3 py-1.5 rounded-full border border-white/15 bg-white/5 backdrop-blur-sm mb-6 fm-fade">
            <span className="text-xs font-medium tracking-wide text-neutral-300">Find something worth watching.</span>
          </div>

          {/* Headline */}
          <h1
            className="font-display text-4xl sm:text-5xl lg:text-7xl font-extrabold tracking-tighter leading-[1.02] fm-fade"
            style={{ animationDelay: "0.1s" }}
          >
            Cinema,<br/>
            <span className="text-[#E50914]">curated for India.</span>
          </h1>

          {/* Subtitle */}
          <p
            className="mt-5 text-base sm:text-lg text-neutral-300 max-w-2xl leading-relaxed fm-fade"
            style={{ animationDelay: "0.18s" }}
          >
            Hand-picked movies & web series across{" "}
            <span className="text-white font-semibold">Hindi, English, Tamil, Telugu, Korean</span>{" "}
            and more.
          </p>

          {/* Search */}
          <div
            className="mt-8 w-full max-w-2xl fm-fade relative z-[60]"
            style={{ animationDelay: "0.26s" }}
          >
            <SearchAutocomplete
              variant="hero"
              placeholder="Search a title, actor, director, or describe a vibe…"
              testid="hero-search-input"
            />

            {/* Quick chips */}
            <div className="mt-4 flex flex-wrap gap-2 text-xs justify-center">
              <span className="text-neutral-400 mr-1 self-center">Try:</span>
              {CHIPS.map((c) => (
                <button
                  type="button"
                  key={c.label}
                  onClick={() => onChip(c)}
                  data-testid={`hero-chip-${c.label.replace(/\s+/g, "-").toLowerCase()}`}
                  className="px-3 py-1.5 rounded-full border border-white/10 hover:border-[#E50914] hover:text-white text-neutral-300 transition-colors font-medium"
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>

          {/* CTA row — 2 buttons only */}
          <div
            className="mt-7 flex flex-col sm:flex-row gap-3 fm-fade"
            style={{ animationDelay: "0.34s" }}
          >
            <Link
              to="/surprise-me"
              data-testid="hero-surprise-btn"
              className="fm-btn-ghost px-6 py-3 rounded-md font-semibold flex items-center justify-center gap-2"
            >
              <Shuffle className="w-4 h-4 text-[#E50914]" /> Surprise Me
            </Link>
            <Link
              to="/concierge"
              data-testid="hero-chat-btn"
              className="fm-btn-red px-7 py-3 rounded-md font-semibold flex items-center justify-center gap-2"
            >
              <MessageCircle className="w-4 h-4" /> Chat with Filmi
            </Link>
          </div>

          {/* ── Filmi Social section ────────────────────────────── */}
          <div
            className="mt-10 w-full max-w-2xl fm-fade"
            style={{ animationDelay: "0.44s" }}
            data-testid="hero-filmi-social"
          >
            {/* Divider with center glow */}
            <div className="relative flex items-center mb-8">
              <div className="flex-1 h-px bg-white/[0.08]" />
              <div
                className="mx-3 w-2 h-2 rounded-full"
                style={{
                  background: "#E50914",
                  boxShadow: "0 0 12px 3px rgba(229,9,20,0.7), 0 0 24px 6px rgba(229,9,20,0.3)",
                }}
              />
              <div className="flex-1 h-px bg-white/[0.08]" />
            </div>

            {/* Section title */}
            <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
              Filmi Social
            </h2>
            <p className="text-sm text-neutral-400 mt-1.5 mb-6">
              Chat. Discuss. Vote. Connect over cinema.
            </p>

            {/* 3 feature cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {SOCIAL_CARDS.map(({ label, to, icon: Icon, color, glow, border, testid }) => (
                <Link
                  key={label}
                  to={to}
                  data-testid={testid}
                  className="group flex flex-col items-center justify-center gap-2 px-3 py-5 rounded-xl bg-[#0A0A0A]/80 backdrop-blur-sm transition-all duration-300 hover:scale-[1.03]"
                  style={{
                    border: `1px solid ${border}`,
                    boxShadow: `0 0 18px ${glow}, inset 0 0 18px ${glow.replace("0.25", "0.06")}`,
                  }}
                >
                  <Icon
                    className="w-6 h-6 shrink-0 transition-transform duration-300 group-hover:scale-110"
                    style={{ color }}
                  />
                  <span className="font-semibold text-sm text-white leading-tight text-center">{label}</span>
                </Link>
              ))}
            </div>
          </div>
          {/* ── end Filmi Social ─────────────────────────────────── */}

        </div>
      </div>
    </section>
  );
}
